import { Info } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Button } from '@/components/ui/button';

const SW_TOAST_ID = 'sw-update-toast';
const SW_UPDATE_INTERVAL = 10 * 60 * 1000; // 10 minutes

export default function UpdateToaster() {
  const { t } = useTranslation();

  const intervalRef = useRef<ReturnType<typeof setInterval>>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swScriptUrl, registration) {
      if (!registration) {
        return;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (!registration.active) {
          return;
        }

        void registration.update();
      }, SW_UPDATE_INTERVAL);
    },
  });

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (needRefresh) {
      toast.custom(
        (tst) => (
          <div className="flex items-center justify-between w-full h-14 py-3 px-4 bg-destructive-subtle pointer-events-auto">
            <div className="flex items-center gap-3">
              <Info className="size-4 text-destructive" strokeWidth={2.5} />
              <span className="text-sm font-medium text-destructive-subtle-foreground">
                {t('errors.cache.toaster.title')}
              </span>
            </div>
            <Button
              onClick={() => {
                toast.dismiss(tst);
                void updateServiceWorker(true);
              }}
            >
              {t('errors.cache.action')}
            </Button>
          </div>
        ),
        {
          duration: Infinity,
          id: SW_TOAST_ID,
        }
      );
    }
  }, [needRefresh, updateServiceWorker, t]);

  return null;
}
